import { cn } from '@mdvip/shared/lib/utils';

const GROUPS = [
  {
    key: 'routing',
    label: 'Routing',
    digits: '⑆ 9 9 9 8 8 7 7 7',
    textClass: 'text-[#9a5410]',
    underlineClass: 'border-b-2 border-b-[#9a5410]',
  },
  {
    key: 'account',
    label: 'Account',
    digits: '0 1 2 3 4 5 6 7 8 9 ⑈',
    textClass: 'text-[#2f8f6b]',
    underlineClass: 'border-b-2 border-b-[#2f8f6b]',
  },
  {
    key: 'check',
    label: 'Check #',
    digits: '1 2 3',
    textClass: 'text-[#8a3d4d]',
    underlineClass: 'border-b-2 border-b-[#8a3d4d]',
  },
] as const;

/**
 * Illustrative schematic of a paper check (routing / account / check number groups), used as the
 * "Where to find your numbers" tooltip content for the bank tab's Routing/Account number fields.
 * Purely decorative — the tooltip trigger's own accessible label carries the a11y meaning, so this
 * renders `aria-hidden`. Kept app-local rather than promoted to shared/blocks since it's a one-off.
 */
export function CheckDiagram({
  heading,
  highlight,
}: {
  /** Translated "Where to find your numbers" copy — mirrors the tooltip trigger's accessible label. */
  heading: string;
  highlight: 'routing' | 'account' | 'check';
}) {
  return (
    <div aria-hidden="true" className="w-[19rem]">
      {/* Inherits the tooltip bubble's own text-popover-foreground — no explicit color needed. */}
      <div className="mb-2 text-xs font-semibold">{heading}</div>
      <div className="rounded-md bg-[#f3ecd8] p-3 text-[#2b2b2b]">
        <div className="flex items-start justify-between text-[8px] font-bold leading-tight">
          <div>
            {/* biome-ignore lint/style/noJsxLiterals: illustrative specimen-check content, not real translatable copy */}
            <div>YOUR NAME</div>
            {/* biome-ignore lint/style/noJsxLiterals: illustrative specimen-check content, not real translatable copy */}
            <div className="font-normal opacity-60">678 Main Street</div>
          </div>
          {/* biome-ignore lint/style/noJsxLiterals: illustrative specimen-check content, not real translatable copy */}
          <div>123</div>
        </div>
        <div className="mt-2 h-px bg-current opacity-30" />
        <div className="mt-1.5 h-px bg-current opacity-30" />
        <div className="mt-3 flex items-end justify-between gap-1.5 font-mono text-[9px]">
          {GROUPS.map((g) => (
            <span
              key={g.key}
              className={cn(
                'pb-0.5',
                g.textClass,
                g.underlineClass,
                highlight === g.key ? 'font-bold' : 'font-normal opacity-70',
              )}
            >
              {g.digits}
            </span>
          ))}
        </div>
        <div className="mt-1 flex justify-between gap-1.5 text-[7px] font-bold uppercase tracking-wide">
          {GROUPS.map((g) => (
            <span key={g.key} className={cn(g.textClass, highlight !== g.key && 'opacity-70')}>
              {g.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
